// Merges the app's research artifacts into one stream of "intel items" that the
// Mission-Impossible popouts display and read aloud: the real gaze-science chain
// (gaze_factors.json), the novelty fringe wow-chain (fringe_wow30.json), and the
// deep-research findings (research_findings.json, added when that job lands).
//
// The cursor is sequential and deterministic (no Math.random, which is banned):
// items are interleaved by kind once at load, then cycled in order.

import { loadGazeChain } from "./gazeChain";
import { trainNano, genLine, nanoReady } from "./nanogen";

export type IntelKind = "science" | "fringe" | "research" | "anomaly" | "readout";

export interface IntelItem {
  kind: IntelKind;
  /** Short HUD tag, e.g. "GAZE 07", "FRINGE", "INTEL". */
  tag: string;
  /** Headline (also the line spoken aloud). */
  title: string;
  /** Longer body line shown under the headline. */
  text: string;
  /** Source label (host or repo/paper name). */
  source: string;
}

let items: IntelItem[] = [];
let cursor = 0;

function host(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

async function fetchJson(path: string): Promise<any | null> {
  try {
    const res = await fetch(path, { cache: "force-cache" });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/** Interleave the three kinds so popouts alternate science / fringe / research. */
function interleave(byKind: IntelItem[][]): IntelItem[] {
  const out: IntelItem[] = [];
  const max = Math.max(0, ...byKind.map((a) => a.length));
  for (let i = 0; i < max; i++) {
    for (const arr of byKind) {
      const it = arr[i];
      if (it) out.push(it);
    }
  }
  return out;
}

/** Load and merge all available intel. Safe to call repeatedly; never throws. */
export async function loadIntel(): Promise<number> {
  const science: IntelItem[] = [];
  const fringe: IntelItem[] = [];
  const research: IntelItem[] = [];
  const anomaly: IntelItem[] = [];

  const gaze = await loadGazeChain();
  for (const g of gaze) {
    science.push({
      kind: "science",
      tag: `GAZE ${String(g.id).padStart(2, "0")}`,
      title: g.wow || g.title,
      text: g.interpret || g.capture,
      source: g.repo?.name || (g.paper ? host(g.paper.url) : g.method),
    });
  }

  const fj = await fetchJson("/fringe_wow30.json");
  for (const f of fj?.factors ?? []) {
    fringe.push({
      kind: "fringe",
      tag: "FRINGE",
      title: f.wow || f.claim,
      text: f.claim || "",
      source: host(f.source_url || ""),
    });
  }

  // Optional: written by the deep-research job. Absent until then (404 -> skip).
  const rj = await fetchJson("/research_findings.json");
  for (const r of rj?.items ?? []) {
    research.push({
      kind: "research",
      tag: "INTEL",
      title: r.title || "",
      text: r.text || "",
      source: r.source || "",
    });
  }

  // Predictive-anomaly fringe pills (SHERLOCK-flavored, always available).
  const aj = await fetchJson("/anomaly_pills.json");
  for (const a of aj?.items ?? []) {
    anomaly.push({
      kind: "anomaly",
      tag: "ANOMALY",
      title: a.title || "",
      text: a.text || "",
      source: a.source || "",
    });
  }

  // Train the on-device nano-generator on everything we loaded, then have it
  // compose a handful of local "readout" lines (phone CPU only, zero server).
  const readout: IntelItem[] = [];
  const corpus = [...anomaly, ...fringe, ...science, ...research].flatMap((it) => [it.title, it.text]);
  trainNano(corpus);
  if (nanoReady()) {
    for (let i = 0; i < 12; i++) {
      const line = genLine(i * 7 + 3);
      if (line.length > 8) {
        readout.push({ kind: "readout", tag: "READOUT", title: line, text: "on-device nano-generator (no server)", source: "phone CPU" });
      }
    }
  }

  items = interleave([readout, anomaly, fringe, science, research]);
  cursor = 0;
  return items.length;
}

/** Next intel item in the cycle, or null if none loaded yet. */
export function nextIntel(): IntelItem | null {
  if (items.length === 0) return null;
  const it = items[cursor % items.length]!;
  cursor++;
  return it;
}

export function intelCount(): number {
  return items.length;
}
